// Client mirror of functions-common/src/charts/engine/filter.ts. Keep in sync.

import type { ChartFilter, ChartRange } from '../types.js'
import {
  getZonedParts,
  shiftCalendarDays,
  shiftCalendarMonths,
  toDate,
  zonedTimeToUtc,
} from './bucket.js'

export function matchFilter(rowValue: unknown, filter: ChartFilter): boolean {
  const { op, value } = filter

  if (Array.isArray(rowValue)) {
    switch (op) {
      case 'is':
        if (value == null) {
          return rowValue.length === 0 || rowValue.some((v) => v == null)
        }
        return rowValue.some((v) => String(v) === String(value))
      case 'isNot':
        if (value == null) {
          return rowValue.length > 0 && rowValue.every((v) => v != null)
        }
        return !rowValue.some((v) => String(v) === String(value))
      case 'in':
        if (!Array.isArray(value)) return false
        return rowValue.some((v) => value.map(String).includes(String(v)))
      case 'isEmpty':
        return rowValue.length === 0
      default:
        // for gt, lt, between, check if any element matches
        return rowValue.some((v) => matchFilter(v, { ...filter, op }))
    }
  }

  switch (op) {
    case 'is':
      if (value == null) return rowValue == null
      return String(rowValue) === String(value)
    case 'isNot':
      if (value == null) return rowValue != null
      return String(rowValue) !== String(value)
    case 'in':
      if (!Array.isArray(value)) return false
      return value.map(String).includes(String(rowValue))
    case 'gt': {
      if (rowValue == null || value == null) return false
      const rowNum = Number(rowValue)
      const valNum = Number(value)
      if (!Number.isNaN(rowNum) && !Number.isNaN(valNum)) {
        return rowNum > valNum
      }
      const rowDate = toDate(rowValue)
      const valDate = toDate(value)
      if (rowDate && valDate) {
        return rowDate.getTime() > valDate.getTime()
      }
      return false
    }
    case 'lt': {
      if (rowValue == null || value == null) return false
      const rowNum = Number(rowValue)
      const valNum = Number(value)
      if (!Number.isNaN(rowNum) && !Number.isNaN(valNum)) {
        return rowNum < valNum
      }
      const rowDate = toDate(rowValue)
      const valDate = toDate(value)
      if (rowDate && valDate) {
        return rowDate.getTime() < valDate.getTime()
      }
      return false
    }
    case 'between': {
      if (!Array.isArray(value) || value.length < 2 || rowValue == null) return false
      const [v0, v1] = value
      const rowNum = Number(rowValue)
      const n0 = Number(v0)
      const n1 = Number(v1)
      if (!Number.isNaN(rowNum) && !Number.isNaN(n0) && !Number.isNaN(n1)) {
        return rowNum >= n0 && rowNum <= n1
      }
      const rowDate = toDate(rowValue)
      const d0 = toDate(v0)
      const d1 = toDate(v1)
      if (rowDate && d0 && d1) {
        const t = rowDate.getTime()
        return t >= d0.getTime() && t <= d1.getTime()
      }
      return false
    }
    case 'isEmpty':
      return rowValue == null || rowValue === ''
    default:
      return true
  }
}

export function resolveRelativeWindow(
  last: number,
  unit: 'day' | 'week' | 'month',
  now: Date,
  timeZone: string
): { from: Date; to: Date } {
  const to = new Date(now.getTime())
  let from: Date

  if (unit === 'day') {
    // Relative 'last N day' = from start of local day N-1 days ago to now
    const daysAgo = last - 1
    const shifted = shiftCalendarDays(to, -daysAgo, timeZone)
    const p = getZonedParts(shifted, timeZone)
    from = zonedTimeToUtc(p.year, p.month, p.day, 0, 0, 0, 0, timeZone)
  } else if (unit === 'week') {
    // Relative 'week' = N * 7 such days
    const daysAgo = last * 7 - 1
    const shifted = shiftCalendarDays(to, -daysAgo, timeZone)
    const p = getZonedParts(shifted, timeZone)
    from = zonedTimeToUtc(p.year, p.month, p.day, 0, 0, 0, 0, timeZone)
  } else {
    // Relative 'month' = same local calendar day N months ago (clamped to month end) to now
    const shifted = shiftCalendarMonths(to, -last, timeZone)
    const p = getZonedParts(shifted, timeZone)
    from = zonedTimeToUtc(p.year, p.month, p.day, 0, 0, 0, 0, timeZone)
  }

  return { from, to }
}

export function resolveAbsoluteWindow(
  fromStr: string,
  toStr: string,
  timeZone: string
): { from: Date; to: Date } | null {
  const fMatch = fromStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
  const tMatch = toStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!fMatch || !tMatch) return null

  const fy = Number(fMatch[1])
  const fm = Number(fMatch[2])
  const fd = Number(fMatch[3])
  const ty = Number(tMatch[1])
  const tm = Number(tMatch[2])
  const td = Number(tMatch[3])

  // from: whole day start 00:00:00.000 local
  const from = zonedTimeToUtc(fy, fm, fd, 0, 0, 0, 0, timeZone)
  // to: whole day end 23:59:59.999 local
  const to = zonedTimeToUtc(ty, tm, td, 23, 59, 59, 999, timeZone)

  return { from, to }
}

export function filterRows(
  rows: Array<Record<string, unknown>>,
  filters: ChartFilter[],
  range: ChartRange,
  timeField: string | undefined,
  nowDate: Date,
  timeZone: string = 'UTC'
): Array<Record<string, unknown>> {
  let rangeWindow: { from: Date; to: Date } | null = null

  if (timeField) {
    if (range.kind === 'relative') {
      rangeWindow = resolveRelativeWindow(range.last, range.unit, nowDate, timeZone)
    } else if (range.kind === 'absolute') {
      rangeWindow = resolveAbsoluteWindow(range.from, range.to, timeZone)
    }
  }

  return rows.filter((row) => {
    // 1. Evaluate user filters
    for (const filter of filters) {
      if (!matchFilter(row[filter.field], filter)) {
        return false
      }
    }

    // 2. Evaluate range filter
    if (rangeWindow && timeField) {
      const rowDate = toDate(row[timeField])
      if (!rowDate) return false
      const rowMs = rowDate.getTime()
      if (rowMs < rangeWindow.from.getTime() || rowMs > rangeWindow.to.getTime()) {
        return false
      }
    }

    return true
  })
}
